import React from 'react';
import { useSelector } from 'react-redux';
import PresignedImage from '../common/PresignedImage';
import FileHoverPreview from '../common/FileHoverPreview';
import LinkHoverPreview from '../common/LinkHoverPreview';
import SharedDragGrip from './SharedDragGrip';
import AttachmentLikeHeart from './AttachmentLikeHeart';
import { useAttachmentTouchInteraction } from '../../utils/sharedFilesTouchDrag';
import { resolveAttachmentId } from '../../utils/sharedFilesDrag';
import { isDisplayableImage, isLinkAttachment, decodeLinkHref } from '../../utils/fileUtils';

const linkFromAttachment = (attachment) => ({
  url: attachment.fileUrl,
  title: attachment.fileName,
  customTitle: attachment.linkCustomTitle,
  description: attachment.linkDescription,
  imageUrl: attachment.linkImageUrl,
  siteName: attachment.linkSiteName,
  domain: attachment.linkDomain,
});

const GroupResourceTile = ({
  attachment,
  index,
  messageId,
  editable,
  canDrag,
  coarsePointer,
  dragIndex,
  overIndex,
  onItemClick,
  onRemove,
  onTouchAttachmentDrop,
  onTouchHighlight,
  canRename = false,
  onRename,
  onItemDragStart,
  onItemDragOver,
  onItemDrop,
  onDragEnd,
  renderFileIcon,
  tileClassName = 'w-[88px] h-[88px]',
  sequenceNumber,
}) => {
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const isImage = isDisplayableImage(attachment);
  const isLink = isLinkAttachment(attachment);
  const linkHref = decodeLinkHref(attachment.fileUrl);
  const payload = {
    sourceMessageId: messageId,
    attachmentId: resolveAttachmentId(attachment),
    itemType: 'attachment',
  };

  const handleOpen = () => {
    if (isLink && linkHref) {
      window.open(linkHref, '_blank', 'noopener,noreferrer');
      return;
    }
    onItemClick?.(index);
  };

  const touchHandlers = useAttachmentTouchInteraction({
    enabled: coarsePointer && editable && canDrag,
    payload,
    onTap: handleOpen,
    onDrop: onTouchAttachmentDrop,
    onHighlight: onTouchHighlight,
  });

  const canMouseDrag = editable && canDrag && !coarsePointer;
  const showDragGrip = editable && canDrag;

  const tileInner = (
    <div
      draggable={canMouseDrag}
      onDragStart={canMouseDrag ? (e) => onItemDragStart(e, index) : undefined}
      onDragEnd={canMouseDrag ? onDragEnd : undefined}
      onDragOver={editable ? (e) => onItemDragOver(e, index) : undefined}
      onDrop={editable ? (e) => onItemDrop(e, index) : undefined}
      onClick={!coarsePointer && isLink ? handleOpen : undefined}
      onDoubleClick={!coarsePointer && !isLink ? () => onItemClick?.(index) : undefined}
      title={isLink ? 'Click to open link' : (canMouseDrag ? 'Drag to move · Double-click to open' : undefined)}
      data-shared-files-drop={messageId}
      data-drop-index={index}
      className={`group/tile relative ${tileClassName} rounded-xl overflow-hidden border-2 transition-shadow ${
        isLink ? 'cursor-pointer' : ''
      } ${
        canMouseDrag ? 'cursor-grab active:cursor-grabbing' : ''
      } ${
        dragIndex === index
          ? 'opacity-50 border-primary-400'
          : overIndex === index
            ? 'border-primary-500 shadow-md ring-2 ring-primary-300/50'
            : 'border-gray-200 dark:border-gray-600'
      }`}
    >
      {isLink ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 p-1">
          {attachment.linkImageUrl ? (
            <img
              src={attachment.linkImageUrl}
              alt=""
              className="w-full h-full object-cover pointer-events-none select-none"
            />
          ) : (
            <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          )}
        </div>
      ) : isImage ? (
        <PresignedImage
          fileKey={attachment.fileUrl}
          alt={attachment.fileName}
          originalName={attachment.fileName}
          className="w-full h-full object-cover pointer-events-none select-none"
          loadingClassName="w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-transparent p-2">
          {renderFileIcon?.(attachment)}
        </div>
      )}

      {(onItemClick || isLink || (coarsePointer && editable && canDrag)) && coarsePointer && (
        <button
          type="button"
          className="absolute inset-0 z-[1] cursor-pointer bg-transparent touch-manipulation"
          aria-label={isLink ? 'Open link' : `Open ${attachment.fileName}`}
          onClick={isLink ? handleOpen : undefined}
          {...(!isLink ? touchHandlers : {})}
        />
      )}

      {sequenceNumber != null && (
        <span className="absolute bottom-1 left-1 z-[2] min-w-[20px] h-5 px-1 rounded-md bg-black/70 text-white text-[11px] font-semibold leading-5 text-center pointer-events-none">
          {sequenceNumber}
        </span>
      )}

      {!isLink && (
        <AttachmentLikeHeart
          attachment={attachment}
          userId={currentUserId}
          size="sm"
          className="absolute bottom-1 right-1 z-[2]"
        />
      )}

      {showDragGrip && (
        <div className="absolute top-0.5 left-0.5 z-[2] hidden group-hover/tile:flex pointer-events-none group-hover/tile:pointer-events-auto">
          <SharedDragGrip
            size="sm"
            title={coarsePointer ? 'Long-press to drag' : 'Drag to move'}
          />
        </div>
      )}

      {editable && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
          className="absolute top-0.5 right-0.5 z-[3] p-0.5 rounded-full bg-black/50 text-white hover:bg-red-600 opacity-0 pointer-events-none transition-opacity group-hover/tile:opacity-100 group-hover/tile:pointer-events-auto"
          title="Remove from group"
          aria-label={`Remove ${attachment.fileName}`}
          draggable={false}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );

  if (isLink) {
    const link = linkFromAttachment(attachment);
    return (
      <LinkHoverPreview
        link={link}
        href={linkHref}
        className="inline-block"
        canRename={canRename}
        onRename={onRename ? (l, title) => onRename(attachment, title) : undefined}
      >
        {tileInner}
      </LinkHoverPreview>
    );
  }

  return (
    <FileHoverPreview
      attachment={attachment}
      className="inline-block"
      canRename={canRename}
      onRename={onRename}
    >
      {tileInner}
    </FileHoverPreview>
  );
};

export default GroupResourceTile;
